import { $DepartureSchedule } from "@store/departure-schedule-store";
import {
  DepartureScheduleT,
  DepartureScheduleHistoryItemT
} from "@interfaces/departure-schedule";
import { $UserAddPermissions, UserPriorityEnum } from "@store/user-store";
import {
  Table,
  TableHead,
  TableBody,
  TableContainer,
  TableRow,
  TableCell,
  Paper,
} from "@mui/material";
import { ArrowDropDownGraySVG } from "@components/svgs";
import { Center, Text } from "@mantine/core";
import React from "react";

import { Loader } from "@ui/indicators";
import clsx from "clsx";
import style from "@scss/components/tables/table-category.module.scss";
import useModal from "@modals/modal-hook";
import { useUnit } from "effector-react";
import { UseSortingResult } from "@hooks/useSorting";
import DepartureScheduleService from "@services/departure-schedule-service";
import DepartureScheduleTableRow from "./departure-schedule-row";
import { useDepartureScheduleQuery } from "@lib/daparture-schedule";
import { Moment } from "moment";

interface Props {
  sorting?: UseSortingResult | undefined;
  isLoading: boolean;
  query: ReturnType<typeof useDepartureScheduleQuery>
}

const DepartureScheduleTable: React.FC<Props> = ({ sorting, isLoading, query }) => {
  const departureSchedule = useUnit($DepartureSchedule);
  const permissions = useUnit($UserAddPermissions);

  const { open } = useModal();

  const handleAddDepartureHistory = (type: "planned" | "full", departureSchedule: DepartureScheduleT) => {
    open("DepartureScheduleHistoryCreate", {
      btnText: "Сохранить",
      modalData: {
        companyId: departureSchedule.company.id,
        title: type === "planned" ? 'Укажите планируемую дату выезда в компанию' : 'Данные выезда',
        type: type,
        isCompany: false
      },
      onConfirm: (date: Moment, companyId: number, prescriptionId: number, dateDaparture: Moment, comment: string, files: File[]) => {
        const formData = new FormData();
        formData.append('companyId', companyId.toString());
        if(type === "planned"){
          formData.append('date', date.format("YYYY-MM-DD"));
        }else if(type === "full"){
          formData.append('prescriptionId', prescriptionId.toString());
          formData.append('date', dateDaparture.format("YYYY-MM-DD"));
          formData.append('comment', comment);
          if(files.length > 0){
            formData.append('file', files[0]);
          }
        }
        DepartureScheduleService.historyCreate(formData, (err, res) => {
          if (err || !res) {
            return Error("При добавлении выезда произошла ошибка");
          }
          if (res.data) {
            query.refetch();
          }
        });
      },
    });
  };

  const handleRemoveHistory = (id: number) => {
    open("ConfirmActionModal", {
      btnText: "Удалить",
      modalData: {
        text: `Вы уверены, что хотите удалить историю выезда #${id}?`,
      },
      onConfirm: () => {
        DepartureScheduleService.historyDelete(id, (err, res) => {
            if (err || !res) {
              return Error("При удалении истории выезда произошла ошибка");
            }
            if (res.data.status) {
              query.refetch();
            }
          }
        );
      },
    });
  };

  const handleEditHistory = (departureScheduleHistory: DepartureScheduleHistoryItemT) => {
    open("DepartureScheduleHistoryCreate", {
      btnText: "Сохранить",
      modalData: {
        companyId: departureScheduleHistory.companyId,
        dateDaparture: departureScheduleHistory.date,
        prescriptionId: departureScheduleHistory.prescriptionId,
        comment: departureScheduleHistory.comment,
        file: departureScheduleHistory.file,
        title: 'Данные выезда',
        type: "full",
        isCompany: false
      },
      onConfirm: (date: Moment, companyId: number, prescriptionId: number, dateDaparture: Moment, comment: string, files: File[]) => {
        const formData = new FormData();
        formData.append('companyId', departureScheduleHistory.companyId.toString());
        formData.append('prescriptionId', prescriptionId.toString());
        formData.append('date', dateDaparture.format("YYYY-MM-DD"));
        formData.append('comment', comment);
        if(files.length > 0){
          formData.append('file', files[0]);
        }
        DepartureScheduleService.historyUpdate(departureScheduleHistory.id, formData, (err, res) => {
          if (err || !res) {
            return Error("При редактировании выезда произошла ошибка");
          }
          if (res.data) {
            query.refetch();
          }
        });
      },
    });
  };

  const handleAcceptedHistory = (accepted: boolean, id: number) => {
    open("ConfirmActionModal", {
      modalData: {
        text: accepted ? 'Вы уверены, что хотите подтвердить выезд?' : 'Вы уверены, что хотите отменить подтверждение выезда?'
      },
      btnText: accepted ? "Подтвердить" : "Отменить",
      onConfirm: () => {
        DepartureScheduleService.historyAccept(id, accepted, (err, res) => {
          if (err || !res) {
            return Error("При подтверждении выезда произошла ошибка");
          }
          if (res.data) {
            query.refetch();
          }
        });
      },
    });
  }

  if (isLoading) {
    return (
      <Center h={950}>
        <Loader />
      </Center>
    );
  }

  if (departureSchedule.length === 0) {
    return (
      <Text mt={40} size="xl" style={{ textAlign: "center", color: "gray" }}>
        Выезды не заполнены
      </Text>
    );
  }

  let isUser = false
  const cellsHeader = [];
  if((permissions.hasRequiredLevel(UserPriorityEnum.Owner) || permissions.hasRequiredLevel(UserPriorityEnum.SuperAdmin))){
    isUser = true;
    cellsHeader.push({
      key: 'users',
      label: 'Сотрудник',
      width: '10%'
    })
  }
  cellsHeader.push({
    key: 'company',
    label: 'Компания',
    width: '10%'
  })
  cellsHeader.push({
    key: 'address',
    label: 'Адрес',
    width: '9%'
  })
  cellsHeader.push({
    key: 'contact',
    label: 'Контактное лицо',
    width: '12%'
  })
  cellsHeader.push({
    key: 'count_departure',
    label: 'Период выезда',
    width: '11%'
  })
  cellsHeader.push({
    key: undefined,
    label: 'Периоды',
    width: '10%'
  })

  return (
    <TableContainer component={Paper}>
      <Table sx={{ width: '100%', tableLayout: 'fixed', whiteSpace: 'pre-line' }} aria-label="caption table">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '8%' }}></TableCell>
            {cellsHeader.map(e => {
              if(e.key){
                return <TableCell sx={{ px: 1, whiteSpace: "nowrap", width: e.width }}>
                        <div
                          className={style.table_head_wrapper}
                          onClick={() => sorting?.onItemSortingChange(`${e.key}Order`)}
                        >
                          <div className={style.table_head_name}>{e.label}</div>
                          <div className={style.table_head_sort} tabIndex={0}>
                            <div
                              className={clsx(style.table_head_sortIcon, {
                                [style.table_head_sortIcon_rotated]:
                                  sorting?.shouldIconRotate(`${e.key}Order`),
                              })}
                            >
                              <ArrowDropDownGraySVG />
                            </div>
                          </div>
                        </div>
                      </TableCell>
              }
              return  <TableCell sx={{ px: 1, whiteSpace: "nowrap" }}>{e.label}</TableCell>
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {!!departureSchedule.length ? (
            <>
              {departureSchedule.map(item =>(
                <DepartureScheduleTableRow
                  item={item}
                  isUser={isUser}
                  onAddHistory={handleAddDepartureHistory}
                  onRemoveHistoryItem={handleRemoveHistory}
                  onEditHistoryItem={handleEditHistory}
                  onAccepted={handleAcceptedHistory}
                />
              ))}
            </>
          ) : (
            <TableRow>
              <TableCell colSpan={
                (permissions.hasRequiredLevel(UserPriorityEnum.Owner) || permissions.hasRequiredLevel(UserPriorityEnum.SuperAdmin)) ? 19 : 18
              } align="center" color="gray">
                <Text>Выезды не заполнены</Text>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DepartureScheduleTable;
